/**
 * German texts the web prototype needs that packages/core/src/i18n/de.ts does
 * not have yet. Kept in one file so they can move into core in one step
 * (packages/ was off-limits while the UI was designed).
 */
import type { EntityType, EventAction, TrashKind } from '../data/types';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const tx = {
  app: {
    done: 'Fertig',
    skipToContent: 'Zum Inhalt springen',
    loading: 'Lädt …',
    offline: (time: string | null) => (time ? `Offline – Stand ${time}` : 'Offline'),
    demoBadge: 'Demo',
    demoBanner: 'Demo-Modus: Beispieldaten, nichts wird an den Server geschickt.',
    retry: 'Erneut versuchen',
    back: 'Zurück',
    close: 'Schließen',
    cancel: 'Abbrechen',
    save: 'Speichern',
    next: 'Weiter',
    open: 'Öffnen',
    more: 'Mehr',
    none: '–',
    yes: 'Ja',
    no: 'Nein',
  },
  login: {
    title: 'Anmelden',
    intro: 'Für Mitglieder der teilnehmenden Vereine.',
    codeSentTo: (email: string) => `Code an ${email} angefordert.`,
    otherAddress: 'Andere Adresse',
    codeHint: '6 Ziffern aus der E-Mail',
    demoTitle: 'Ohne Konto ausprobieren',
    demoButton: 'Demo ansehen',
    demoAs: 'Als',
    demoHint: 'Beispieldaten im Browser, kein Server nötig.',
    privacy:
      'Gespeichert werden E-Mail-Adresse, Name, Rolle und was du erfasst. Keine Werbung, kein Tracking, keine Cookies.',
    privacyLink: 'Datenschutz',
    termsLink: 'Nutzungsbedingungen',
    invalidEmail: 'Bitte eine gültige E-Mail-Adresse eingeben.',
    invalidCodeFormat: 'Der Code hat 6 Ziffern.',
    kicker: 'Schadensregister der Vereine',
    // Unused since the Vereinsflieger sign-in went (06.10.2026); kept because the
    // shipped announcement 2026-10-05-vf-email-confirm names it (news/texts.ts).
    vfEmailCode: 'Code aus der E-Mail',
  },
  privacy: {
    title: 'Datenschutz',
    // The statement itself lives in src/legal/texts.ts.
    clubTitle: 'Verantwortlich für die Daten dieses Vereins',
    clubNone: 'Der Verein hat noch keine Kontaktdaten hinterlegt.',
  },
  fleet: {
    title: 'Flotte',
    subtitle: 'Antippen öffnet den Lageplan.',
    open: (n: number) => `${n} ${plural(n, 'offener Schaden', 'offene Schäden')}`,
    noneOpen: 'Keine offenen Schäden',
    lastReport: 'Letzte Meldung',
    never: 'noch keine',
    otherViews: (n: number) => `+${n} in anderen Ansichten`,
    inactive: 'Außer Dienst',
  },
  plan: {
    viewSwitcher: 'Ansicht',
    mirrored: 'gespiegelt',
    mirroredOf: (base: string) => `gespiegelt aus ${base}`,
    zoomIn: 'Vergrößern',
    zoomOut: 'Verkleinern',
    zoomReset: 'Ganze Zeichnung',
    tapHint: 'Auf die Zeichnung tippen, um einen Schaden zu melden.',
    tapHintEditor: 'Auf die Zeichnung tippen, um einen Schaden zu erfassen.',
    placeHintSet: 'Kreuz gedrückt halten und ziehen, um es zu verschieben, oder woanders neu tippen.',
    placeConfirm: 'Hier melden',
    placeConfirmEditor: 'Hier erfassen',
    placeDiscard: 'Verwerfen',
    keyboardHint: 'Pfeiltasten verschieben, + und − zoomen, Eingabe setzt einen Pin in der Mitte.',
    board: (reg: string, view: string) => `Risszeichnung ${reg}, Ansicht ${view}`,
    pinLabel: (number: string, category: string, status: string) => `${number} ${category}, ${status}`,
    clusterLabel: (n: number) => `${n} Schäden an dieser Stelle`,
    cluster: (n: number) => `${n} Schäden an dieser Stelle`,
    legend: 'Legende',
    filter: 'Status filtern',
    listToggle: 'Liste',
    viewCount: (n: number) => (n === 0 ? 'keine Schäden im Filter' : n === 1 ? '1 Schaden im Filter' : `${n} Schäden im Filter`),
    planToggle: 'Plan',
    listTitle: (n: number) => `${n} ${plural(n, 'Schaden', 'Schäden')}`,
    listEmpty: 'Keine Schäden mit diesem Filter.',
    allViews: 'Alle Ansichten',
    thisView: 'Diese Ansicht',
    placeholderDrawing: 'Platzhalter-Zeichnung',
    credit: 'Quelle',
    toDetail: 'Zum Schaden',
    notFound: 'Dieses Flugzeug gibt es nicht.',
    provisional: 'Neuer Pin',
    // Asked on every report on the top and bottom views (owner, 17.10.2026).
    standpoint: {
      question: 'Wo stehst du?',
      why: 'Damit die Zeichnung so liegt, wie du das Flugzeug vor dir siehst – und der Schaden auf der richtigen Seite landet.',
      nose: 'Ich stehe vor der Nase',
      tail: 'Ich stehe hinter dem Heck',
      noseShown: 'Gedreht: Nase unten – so wie vor der Nase gesehen',
      tailShown: 'Nase oben – so wie hinter dem Heck gesehen',
      watermark: 'Nase unten',
      change: 'Standort ändern',
    },
  },
  video: {
    add: 'Video aufnehmen',
    fromLibrary: 'Video aus Galerie',
    preparing: (percent: number) => `Video wird vorbereitet … ${percent} %`,
    uploading: (percent: number) => `Video wird hochgeladen … ${percent} %`,
    notVideo: 'Diese Datei enthält kein Video.',
    tooLong: (max: number, actual: number) => `Das Video ist ${actual} Sekunden lang; höchstens ${max} Sekunden. Bitte vorher kürzen.`,
    tooLarge: 'Das Video ist auch verkleinert noch zu groß. Bitte kürzer aufnehmen.',
    cannotConvert: 'Dieses Video kann der Browser nicht verarbeiten. Bitte in der App aufnehmen.',
    tooMany: (max: number) => `Höchstens ${max} Videos je Schaden.`,
    label: (n: number) => `Video ${n}`,
    duration: (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`,
  },
  create: {
    nearbyTitle: 'Schon bekannt?',
    nearbyIntro: 'Prüfe zuerst, ob es einer dieser Einträge ist.',
    toExisting: 'Zum bestehenden Eintrag',
    newAnyway: 'Nein, neuer Schaden',
    sameTitle: (number: string) => `Fotos zu ${number} hinzufügen`,
    sameIntro:
      'Die Fotos werden dem bestehenden Schaden zugeordnet. Es entsteht kein neuer Eintrag.',
    sameNote: 'Notiz (optional)',
    sameSave: 'Zuordnen',
    reportTitle: 'Schaden melden',
    createTitle: 'Schaden erfassen',
    editTitle: 'Schaden bearbeiten',
    submitMember: 'Melden',
    submitEditor: 'Speichern',
    memberStatusNote: 'Deine Meldung erscheint als „Gemeldet“, bis das Technikteam sie gesichtet hat.',
    titlePlaceholder: 'z. B. Kratzer an der Flügelvorderkante',
    descriptionPlaceholder: 'Größe, Tiefe, seit wann bekannt …',
    position: (reg: string, view: string) => `${reg} · Ansicht ${view}`,
    categoryRequired: 'Bitte mindestens eine Kategorie wählen.',
    categoriesHint: 'Mehrere möglich, z. B. Kratzer und Lackschaden.',
    addPhotoCamera: 'Foto aufnehmen',
    addPhotoGallery: 'Aus Galerie',
    photoHint: 'Empfohlen: ein Übersichtsfoto aus 1–2 m und ein Detailfoto.',
    processing: 'Foto wird verkleinert …',
    removePhoto: 'Foto entfernen',
    tooManyPhotos: (max: number) => `Höchstens ${max} Fotos je Schaden.`,
    photoFailed: 'Dieses Foto konnte nicht gelesen werden.',
    overviewToggle: 'Übersicht',
  },
  detail: {
    position: 'Position',
    view: 'Ansicht',
    aircraft: 'Flugzeug',
    noDescription: 'Keine Beschreibung.',
    noPhotos: 'Noch keine Fotos.',
    photoOf: (i: number, n: number) => `Foto ${i} von ${n}`,
    prev: 'Vorheriges Foto',
    next: 'Nächstes Foto',
    overview: 'Übersicht',
    reviewed: (name: string, date: string) => `Gesichtet von ${name} am ${date}`,
    mergedInto: (number: string) => `Zusammengeführt in ${number}`,
    mergedFrom: 'Zusammengeführte Meldungen',
    commentPlaceholder: 'Rückfrage oder Hinweis …',
    commentSend: 'Senden',
    commentLabel: 'Rückfrage/Hinweis schreiben',
    commentDelete: 'Löschen',
    commentDeleteLabel: (author: string) => `Rückfrage/Hinweis von ${author} löschen`,
    noComments: 'Noch keine Rückfragen/Hinweise.',
    deleteDamage: 'Löschen',
    withdraw: 'Meldung zurückziehen',
    deleteConfirm: 'Schaden in den Papierkorb legen?',
    reviewActions: 'Sichtung und Status',
    openPlan: 'Im Lageplan zeigen',
    acceptedNotice: 'Bleibt so: Die Technik hat diesen Schaden geprüft und entschieden, ihn nicht zu reparieren.',
    deletedNotice: 'Dieser Schaden liegt im Papierkorb.',
    addPhotos: 'Fotos/Videos hinzufügen',
    removeImage: 'Foto entfernen',
    // Technik corrects where a damage sits; title, photos and comments stay (owner, 17.10.2026).
    move: {
      action: 'Position ändern',
      title: 'Position ändern',
      hint: 'Ansicht wählen und auf die richtige Stelle tippen. Fotos, Beschreibung und Verlauf bleiben erhalten.',
      turn: 'Zeichnung drehen (Nase unten)',
      save: 'Position speichern',
      unchanged: 'Die Position ist unverändert.',
      saved: 'Position geändert.',
      offline: 'Position ändern geht nur mit Verbindung.',
    },
  },
  action: {
    confirm: 'Bestätigen',
    observe: 'Beobachten',
    repair: 'Reparatur geplant',
    accept: 'Akzeptieren',
    resolve: 'Behoben',
    reject: 'Verwerfen',
    merge: 'Zusammenführen',
    reopen: 'Wieder öffnen',
  },
  reason: {
    rejectTitle: 'Meldung verwerfen',
    acceptTitle: 'Akzeptieren – bleibt so',
    acceptHint: 'Der Schaden bleibt sichtbar und wird bewusst nicht repariert. Die Begründung lesen alle, die ihn beim Außencheck antippen.',
    mergeTitle: 'Zusammenführen mit …',
    mergeTarget: 'Bestehender Schaden',
    mergeNoCandidates: 'Keine Schäden in der Nähe. Schadensnummer eingeben (z. B. 42):',
    mergeDefault: (number: string) => `Duplikat von ${number}`,
    count: (n: number, min: number) => (min > 1 ? `${n} / mindestens ${min} Zeichen` : n > 0 ? `${n} Zeichen` : 'Begründung erforderlich'),
  },
  toast: {
    tenantCreated: (name: string, email: string) => `${name} angelegt. Einladung an ${email}.`,
    tenantSaved: 'Vereinsdaten gespeichert.',
    tenantActivated: 'Verein aktiviert.',
    tenantDeactivated: 'Verein deaktiviert.',
    adminAdded: (name: string) => `${name} ist jetzt Administrator.`,
    notDelivered: 'Die Einladung konnte nicht per E-Mail zugestellt werden – bitte selbst Bescheid geben.',
    memberAdded: (name: string) => `${name} eingeladen.`,
    created: (n: string) => `${n} gespeichert.`,
    reported: (n: string) => `${n} gemeldet. Danke!`,
    updated: 'Änderung gespeichert.',
    statusChanged: (status: string) => `Status: ${status}.`,
    merged: (n: string) => `Zusammengeführt mit ${n}.`,
    deleted: 'In den Papierkorb gelegt.',
    restored: 'Wiederhergestellt.',
    photosAdded: (n: number) => `${n} ${plural(n, 'Foto', 'Fotos')} hinzugefügt.`,
    photoRemoved: 'Foto entfernt.',
    commentAdded: 'Rückfrage/Hinweis gesendet.',
    commentDeleted: 'Rückfrage/Hinweis gelöscht.',
    undone: 'Rückgängig gemacht.',
    undoFailed: 'Rückgängig ging nicht mehr.',
    memberUpdated: 'Mitglied geändert.',
    aircraftUpdated: 'Flugzeug geändert.',
    aircraftCreated: (reg: string) => `${reg} angelegt.`,
    aircraftDeleted: (reg: string) => `${reg} gelöscht.`,
    aircraftRestored: (reg: string) => `${reg} wiederhergestellt.`,
    typeCreated: (code: string) => `Muster ${code} angelegt.`,
    typeSaved: 'Muster gespeichert.',
    drawingSaved: 'Zeichnung gespeichert.',
    anonymized: 'Person anonymisiert.',
    emailChanged: (name: string) => `Adresse von ${name} geändert.`,
    emailAdded: 'Weitere Adresse hinzugefügt.',
    emailRemoved: 'Adresse entfernt.',
    settingsSaved: 'Einstellung gespeichert.',
    sessionsEnded: 'Alle Sitzungen beendet.',
    imported: (a: number, u: number, d: number) =>
      `Import: ${a} neu, ${u} geändert, ${d} deaktiviert.`,
    invitationsQueued: (n: number) =>
      `${n} ${plural(n, 'Einladung wird', 'Einladungen werden')} nach und nach per E-Mail verschickt.`,
    inviteQueued: (name: string) => `Die Einladung an ${name} geht in den nächsten Minuten raus.`,
    demoReset: 'Demodaten zurückgesetzt.',
    storageFull:
      'Der Browserspeicher ist voll. Große Fotos werden nach dem Neuladen durch Vorschaubilder ersetzt.',
  },
  search: {
    title: 'Suche',
    // Short enough for a phone's single line; what is searched is spelled out in minChars below.
    placeholder: 'Titel, Nummer, Kennzeichen …',
    label: 'Suchbegriff',
    minChars: 'Sucht ab 2 Zeichen in Titel, Beschreibung, Schadensnummer und Kennzeichen.',
    results: (n: number) => `${n} ${plural(n, 'Treffer', 'Treffer')}`,
    empty: 'Nichts gefunden. Filter lockern?',
    filters: 'Filter',
    reset: 'Filter zurücksetzen',
    anyAircraft: 'Alle Flugzeuge',
    anyCategory: 'Alle Kategorien',
    anyView: 'Alle Ansichten',
    period: 'Zeitraum',
    periodAll: 'Gesamt',
    periodDays: (d: number) => (d === 365 ? '12 Monate' : `${d} Tage`),
    withPhoto: 'nur mit Foto',
    exactNumber: (n: string) => `Kennung ${n}`,
  },
  inbox: {
    title: 'Eingang',
    subtitle: 'Meldungen von Mitgliedern, älteste zuerst.',
    empty: 'Alles gesichtet. Keine offenen Meldungen.',
    stats: (open: number, oldest: string | null) =>
      oldest ? `${open} offen · älteste ${oldest}` : `${open} offen`,
    age: (days: number) => (days === 0 ? 'heute' : days === 1 ? 'seit 1 Tag' : `seit ${days} Tagen`),
    nearby: (n: number) => (n === 0 ? 'nichts in der Nähe' : `${n} in der Nähe`),
    allAircraft: 'Alle',
    statOpen: 'Offen',
    statOldest: 'Älteste',
    photos: (n: number) => `${n} ${plural(n, 'Foto', 'Fotos')}`,
  },
  profile: {
    title: 'Profil',
    role: 'Rolle',
    email: 'E-Mail',
    mode: 'Anmeldung',
    modeDemo: 'Demo-Modus (ohne Server)',
    modeApi: 'Sitzung am Server',
    myReports: 'Meine Meldungen',
    myReportsEmpty: 'Du hast noch nichts gemeldet.',
    /** Heading of the card with help, privacy statement and imprint. */
    info: 'Hilfe und Rechtliches',
    demoRole: 'Demo-Rolle wechseln',
    demoReset: 'Demodaten zurücksetzen',
    demoResetConfirm: 'Alle Änderungen an den Demodaten verwerfen?',
    display: 'Darstellung',
    highContrast: 'Hoher Kontrast (Sonne)',
    highContrastHint: 'Kräftigere Linien und Farben für draußen.',
    admin: 'Verwaltung',
    reviewNote: 'Notiz der Technik',
    platform: 'Plattform',
    memberships: 'Vereine',
    roleIn: (club: string) => `Rolle in ${club}`,
    noTenant: 'Kein Verein ausgewählt.',
    /** „E-Mail-Adressen“ in the profile (0023): the person's own sign-in addresses. */
    emails: {
      title: 'E-Mail-Adressen',
      primary: 'Erste Adresse',
      primaryHint: 'Einladungen und Hinweise deines Vereins gehen an diese Adresse.',
      further: 'Weitere Adressen',
      hint: 'Anmelden kannst du dich mit jeder dieser Adressen; der Code kommt an die Adresse, die du eingibst.',
      none: 'Keine weiteren Adressen.',
      add: 'Weitere Adresse hinzufügen',
      newLabel: 'Weitere E-Mail-Adresse',
      send: 'Code senden',
      sent: 'Ist die Adresse noch frei, ist ein Bestätigungscode dorthin unterwegs.',
      pending: (email: string) => `Wartet auf Bestätigung: ${email}`,
      codeLabel: 'Bestätigungscode',
      confirm: 'Bestätigen',
      remove: 'Entfernen',
      removeLabel: (email: string) => `${email} entfernen`,
      removeConfirm: (email: string) =>
        `${email} entfernen? Wer mit dieser Adresse angemeldet ist, wird auf diesem Weg sofort abgemeldet.`,
      byClub: 'vom Verein eingetragen',
      bySelf: 'von dir bestätigt',
      demoCode: (code: string) => `In der Vorführung geht keine E-Mail hinaus: Der Code ist ${code}.`,
    },
  },
  tenant: {
    switcher: 'Verein wechseln',
    current: 'Aktueller Verein',
    allClubs: 'Alle Vereine',
    pickerTitle: 'Deine Vereine',
    pickerIntro: 'Du bist Mitglied in folgenden Vereinen:',
    pickerRemember: 'Die Auswahl wird auf diesem Gerät gemerkt.',
    noneTitle: 'Kein Verein freigeschaltet',
    noneBody: 'Dein Zugang ist gültig, aber in keinem aktiven Verein freigeschaltet. Bitte wende dich an den Administrator deines Vereins.',
    toPlatform: 'Zur Plattform',
    errors: {
      nameRequired: 'Bitte einen Namen mit mindestens 2 Zeichen eingeben.',
      tooLong: (n: number) => `Höchstens ${n} Zeichen.`,
      slugInvalid: '3–40 Zeichen: Kleinbuchstaben a–z, Ziffern und Bindestriche.',
      slugTaken: 'Dieser Kurzname ist schon vergeben.',
      slugFixed: 'Der Kurzname kann nach dem Anlegen nicht mehr geändert werden.',
      codeInvalid: '2–5 Großbuchstaben A–Z, z. B. LSGB.',
      codeTaken: 'Dieses Kürzel ist schon vergeben.',
      codeFixed: 'Das Kürzel kann nach dem Anlegen nicht mehr geändert werden.',
      emailInvalid: 'Bitte eine gültige E-Mail-Adresse eingeben.',
      websiteInvalid: 'Bitte eine vollständige Adresse mit https:// eingeben.',
      adminNameRequired: 'Bitte den Namen des Administrators eingeben.',
      alreadyAdmin: 'Diese Person ist bereits Administrator dieses Vereins.',
    },
    fields: {
      name: 'Name',
      nameHint: 'So erscheint der Verein in der Kopfzeile und bei der Anmeldung.',
      slug: 'Kurzname (in Links)',
      slugHint: (slug: string) => `Adresse: /v/${slug || '…'} – nach dem Anlegen nicht mehr änderbar.`,
      slugFixed: 'Kurzname',
      slugFixedHint: 'Steht in allen Links und ist deshalb nicht änderbar.',
      code: 'Kürzel (in Schadensnummern)',
      codeHint: (code: string) => `Schadensnummern: ${code || '…'}-000001 – nach dem Anlegen nicht mehr änderbar.`,
      codeFixed: 'Kürzel',
      codeFixedHint: (code: string) => `Steht in jeder Schadensnummer (${code}-000042) und ist deshalb nicht änderbar.`,
      contactGroup: 'Kontaktdaten (optional)',
      clubName: 'Vereinsname',
      clubNameHint: 'Vollständiger Name laut Vereinsregister, falls abweichend.',
      street: 'Straße',
      postalCode: 'PLZ',
      city: 'Ort',
      country: 'Land',
      phone: 'Telefon',
      contactEmail: 'E-Mail',
      website: 'Website',
      notes: 'Notiz',
      notesHint: 'Nur für die Plattform sichtbar, nicht für den Verein.',
      adminGroup: 'Erster Administrator',
      adminHint: 'Bekommt eine Einladung per E-Mail und verwaltet danach Mitglieder und Flotte selbst.',
      adminName: 'Name',
      adminEmail: 'E-Mail',
    },
  },
  platform: {
    title: 'Plattform',
    nav: 'Plattform',
    subtitle: 'Vereine anlegen und verwalten. Schäden, Fotos und Mitgliederlisten eines Vereins siehst du nur, wenn du dort selbst Mitglied bist.',
    create: 'Verein anlegen',
    createTitle: 'Neuer Verein',
    createSave: 'Verein anlegen',
    count: (n: number) => `${n} ${plural(n, 'Verein', 'Vereine')}`,
    empty: 'Noch keine Vereine.',
    search: 'Verein suchen',
    colName: 'Verein',
    colCity: 'Ort',
    colStatus: 'Status',
    colCounts: 'Umfang',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    counts: (m: number, a: number, d: number) => `${m} ${plural(m, 'Mitglied', 'Mitglieder')} · ${a} ${plural(a, 'Flugzeug', 'Flugzeuge')} · ${d} ${plural(d, 'Schaden', 'Schäden')}`,
    countsShort: { members: 'Mitglieder', aircraft: 'Flugzeuge', damages: 'Schäden' },
    back: 'Alle Vereine',
    detailMaster: 'Stammdaten',
    createdAt: (date: string) => `Angelegt am ${date}`,
    open: 'Verein öffnen',
    openHint: 'Du bist dort Mitglied.',
    notMember: 'Du bist in diesem Verein nicht Mitglied und siehst deshalb keine Schäden.',
    status: 'Status',
    statusActiveHint: 'Mitglieder können sich anmelden und arbeiten.',
    statusInactiveHint: 'Niemand kann den Verein öffnen; die Daten bleiben erhalten.',
    deactivate: 'Verein deaktivieren',
    activate: 'Verein aktivieren',
    deactivateTitle: (name: string) => `${name} deaktivieren?`,
    deactivateBody: 'Mitglieder können den Verein danach nicht mehr öffnen. Es wird nichts gelöscht; Aktivieren macht alles wieder zugänglich.',
    activateTitle: (name: string) => `${name} aktivieren?`,
    activateBody: 'Mitglieder können den Verein danach wieder öffnen.',
    admins: 'Administratoren',
    adminsHint: 'Administratoren verwalten Mitglieder, Flotte und Einstellungen ihres Vereins.',
    addAdmin: 'Administrator hinzufügen',
    addAdminSave: 'Hinzufügen',
    addAdminHint: 'Ist die Person schon Mitglied, wird ihre Rolle auf Administrator gesetzt. Sonst wird sie angelegt und eingeladen.',
    noAdmins: 'Kein aktiver Administrator.',
    notFound: 'Diesen Verein gibt es nicht.',
    slugSuggest: (slug: string) => `Vorschlag „${slug}“ übernehmen`,
    codeSuggest: (code: string) => `Vorschlag „${code}“ übernehmen`,
    saveMaster: 'Stammdaten speichern',
    noChanges: 'Keine Änderungen.',
  },
  club: {
    tab: 'Verein',
    title: 'Vereinsdaten',
    intro: 'Kontaktdaten deines Vereins. Name und Kurzname legt die Plattform fest.',
    name: 'Name',
    save: 'Kontaktdaten speichern',
  },
  admin: {
    title: 'Verwaltung',
    members: {
      search: 'Mitglied suchen',
      name: 'Name',
      email: 'E-Mail',
      role: 'Rolle',
      active: 'Aktiv',
      lastSignIn: 'Letzte Anmeldung',
      providers: 'Anmeldewege',
      deactivate: 'Deaktivieren',
      activate: 'Aktivieren',
      endSessions: 'Sitzungen beenden',
      add: 'Mitglied hinzufügen',
      addSave: 'Hinzufügen',
      count: (n: number) => `${n} ${plural(n, 'Mitglied', 'Mitglieder')}`,
      csvTitle: 'CSV importieren',
      csvHelp: 'Entweder ohne Kopfzeile in der Form email;name;rolle (Rolle als Mitglied, Technik oder Administrator) – oder eine Datei mit Kopfzeile, etwa die Mitgliederliste aus Vereinsflieger. Gefunden werden die Spalten E-Mail, Vorname und Nachname (oder Name), Rolle und Status.',
      csvPaste: 'CSV einfügen oder Datei wählen',
      csvFile: 'Datei wählen',
      csvPreview: 'Vorschau',
      csvNew: 'Neu',
      csvChanged: 'Geändert',
      csvUnchanged: 'Unverändert',
      csvDeactivated: 'Würden deaktiviert',
      csvInvalid: 'Fehlerhafte Zeilen',
      csvDeactivateMissing: 'Fehlende Adressen deaktivieren',
      csvInvite: 'Neu Aufgenommene per E-Mail einladen',
      csvInviteHint: (perHour: number) =>
        `Die Einladungen gehen nach und nach raus, höchstens ${perHour} pro Stunde – damit Anmeldecodes jederzeit durchkommen. Bei vielen neuen Mitgliedern dauert es also eine Weile.`,
      invitation: 'Einladung',
      invitationPending: 'ausstehend',
      invitationSent: (when: string) => `verschickt ${when}`,
      invitationFailed: 'nicht zustellbar',
      invite: 'Einladung senden',
      inviteAgain: 'Erneut einladen',
      csvApply: 'Import ausführen',
      csvNothing: 'Keine Änderungen.',
      csvColumns: (list: string) => `Erkannte Spalten: ${list}`,
      csvStatusTitle: 'Wer gilt als Mitglied?',
      csvStatusHint: 'Die Datei hat eine Statusspalte. Übernommen werden nur Zeilen mit den gewählten Werten; wer mit einem anderen Status schon Mitglied ist, wird deaktiviert.',
      csvStatusEmpty: '(leer)',
      csvStatusCount: (n: number) => `${n} ${plural(n, 'Zeile', 'Zeilen')}`,
      csvDeactivatedByStatus: 'Werden deaktiviert (Status)',
      csvExcluded: 'Nicht übernommen (Status)',
      csvRoleIgnored: (column: string) =>
        `Die Spalte „${column}“ enthält keine der Rollen Mitglied, Technik oder Administrator und wird nicht übernommen. Neue Personen werden Mitglied, vorhandene behalten ihre Rolle.`,
      csvNoRoleColumn: 'Ohne Rollenspalte: neue Personen werden Mitglied, vorhandene behalten ihre Rolle.',
      // 'vereinsflieger' only for identities from before 06.10.2026 (ADR 0002).
      providerLabel: { 'email-otp': 'E-Mail', vereinsflieger: 'Vereinsflieger' } as Record<string, string>,
      changeEmail: 'Adresse ändern',
      changeEmailTitle: (name: string) => `Adresse von ${name} ändern`,
      changeEmailNew: 'Neue E-Mail-Adresse',
      changeEmailHint:
        'Angemeldet wird mit einem Code an diese Adresse. Nimm am besten die Adresse, die auch in Vereinsflieger steht – dann passt auch der nächste Import.',
      changeEmailConsequence: 'Die bisherige Adresse gilt sofort nicht mehr: Die Person wird auf allen Geräten abgemeldet und meldet sich mit der neuen Adresse wieder an.',
      changeEmailSave: 'Adresse ändern',
      // Further addresses (0023).
      additional: 'Weitere Adressen',
      additionalAdd: 'Weitere Adresse',
      additionalTitle: (name: string) => `Weitere Adressen von ${name}`,
      additionalNew: 'Weitere E-Mail-Adresse',
      additionalHint:
        'Mit jeder weiteren Adresse kann sich die Person ebenfalls anmelden – sofort, ohne Bestätigung. Trag nur Adressen ein, die der Person gehören. Einladungen gehen weiter an die erste Adresse; der Import erkennt die Person an jeder ihrer Adressen.',
      additionalSave: 'Hinzufügen',
      additionalRemove: 'Entfernen',
      additionalRemoveLabel: (email: string) => `${email} entfernen`,
      additionalRemoveConsequence: 'Eine entfernte Adresse gilt sofort nicht mehr; wer mit ihr angemeldet ist, wird abgemeldet.',
      additionalNone: 'Noch keine weiteren Adressen.',
      additionalBySelf: 'von der Person bestätigt',
      anonymize: 'Anonymisieren',
      anonymized: 'Anonymisiert',
      anonymizeTitle: (name: string) => `${name} anonymisieren?`,
      anonymizeIntro: 'Für Löschanfragen (Recht auf Löschung). Das lässt sich nicht rückgängig machen:',
      anonymizeConsequences: [
        'Name und E-Mail-Adresse werden durch „geloescht-…“ ersetzt, im ganzen System.',
        'Die Mitgliedschaft endet, alle Sitzungen werden beendet; Anmelden geht nicht mehr.',
        'Meldungen, Fotos und Rückfragen/Hinweise bleiben beim Verein, als „geloescht-…“.',
        'Ältere Einträge im Protokoll und im Änderungsverlauf werden beim nächsten wöchentlichen Lauf bereinigt.',
      ] as readonly string[],
      anonymizeConfirm: 'Endgültig anonymisieren',
      anonymizeSelf: 'Sich selbst kann man nicht anonymisieren.',
      anonymizeLastAdmin: 'Der letzte Administrator kann nicht anonymisiert werden.',
    },
    fleet: {
      registration: 'Kennzeichen',
      type: 'Muster',
      active: 'Im Dienst',
      drawings: 'Risszeichnungen',
      placeholder: 'Platzhalter',
      credit: 'Quelle',
      tabAircraft: 'Flugzeuge',
      tabTypes: 'Muster',
      tabsLabel: 'Flotte: Flugzeuge oder Muster',
      damages: 'Schäden',
      actions: 'Aktionen',
      sortOrder: 'Reihenfolge',
      sortOrderHint: 'Optional. Ohne Angabe hinter das letzte Flugzeug.',
      addAircraft: 'Flugzeug hinzufügen',
      editAircraft: (reg: string) => `${reg} bearbeiten`,
      save: 'Speichern',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      restore: 'Wiederherstellen',
      deleted: 'Gelöscht',
      countAircraft: (live: number, deleted: number) =>
        `${live} ${plural(live, 'Flugzeug', 'Flugzeuge')}${deleted ? ` · ${deleted} gelöscht` : ''}`,
      registrationHint: 'D-ABCD oder D-1234',
      registrationInvalid: 'Kennzeichen im Format D-ABCD oder D-1234.',
      registrationLocked: 'Fest, weil es zu diesem Flugzeug schon Schäden gibt: das Kennzeichen steht in deren Protokoll, in Links und auf Zetteln.',
      lockedTitle: 'Kennzeichen fest – es gibt schon Schäden',
      typeChoose: 'Muster wählen …',
      typeOwnGroup: 'Eigene Muster',
      typeCatalogueGroup: 'Katalog',
      typePlaceholderSuffix: ' – Platzhalter',
      typeChangeHint: 'Pins vorhandener Schäden können verrutschen, wenn die Zeichnungen des neuen Musters andere Proportionen haben.',
      noDeleteTitle: 'Flugzeug hat Schäden – stattdessen außer Dienst stellen',
      deleteTitle: (reg: string) => `${reg} löschen?`,
      deleteBody: 'Das Flugzeug hat keine Schäden und verschwindet aus Flotte und Auswahl. Es landet im Papierkorb und lässt sich dort oder hier wiederherstellen.',
      restoreFromError: 'Gelöschtes Flugzeug wiederherstellen',
      driftTitle: 'Pins würden verrutschen',
      driftIntro: 'Pins sind relativ zur Zeichnung gespeichert. Ändert sich das Seitenverhältnis einer Ansicht, auf der es Schäden gibt, sitzen deren Pins danach an einer anderen Stelle des Flugzeugs.',
      driftForce: 'Trotzdem übernehmen',
      driftAdvice: 'Besser: eine Zeichnung mit denselben Proportionen wählen. Übernommen wird die Begründung im Protokoll festgehalten.',
      addType: 'Muster anlegen',
      typeCode: 'Kürzel',
      typeCodeHint: '2–16 Zeichen: Großbuchstaben, Ziffern, Bindestrich (z. B. ASK21). Danach fest.',
      typeCodeInvalid: 'Kürzel: 2 bis 16 Zeichen, Buchstaben, Ziffern und Bindestrich.',
      typeName: 'Name',
      typeNameHint: 'z. B. Schleicher ASK 21',
      typeCredit: 'Quellenangabe',
      typeCreditHint: 'Pflicht: woher die Zeichnungen stammen (Handbuch, eigene Zeichnung, Lizenz).',
      catalogue: 'Katalog',
      own: 'Eigenes Muster',
      aircraftUsing: (n: number) => `${n} ${plural(n, 'Flugzeug', 'Flugzeuge')}`,
      complete: 'Vollständig',
      incomplete: (n: number) => `${n} von 3 Grundansichten`,
      openEditor: 'Zeichnungen',
      catalogueReadOnly: 'Muster aus dem Katalog sind nur lesbar.',
      editorBack: 'Flotte',
      editorTitle: (code: string) => `Muster ${code}`,
      editorSaveMeta: 'Name und Quelle speichern',
      progress: (n: number) => `${n} von 3 Grundansichten`,
      completeNow: 'Muster vollständig – die Flugzeuge dieses Musters zeigen jetzt die Zeichnungen.',
      incompleteHint: 'Bis alle drei Grundansichten (Oben, Vorne, Links) da sind, zeigt die App für dieses Muster überall die Platzhalter-Silhouette.',
      source: { bundled: 'Katalog', own: 'Eigenes Bild', mirrored: 'Gespiegelt', missing: 'Fehlt' } as Record<string, string>,
      mirroredFrom: (view: string) => `gespiegelt aus ${view}`,
      upload: 'Hochladen',
      replace: 'Ersetzen',
      uploadOwn: 'Eigenes Bild hochladen',
      uploadTitle: (view: string) => `Zeichnung ${view}`,
      chooseFile: 'PNG-Datei wählen',
      fileHint: 'PNG mit transparentem Hintergrund, längere Seite 400–4000 px, höchstens 5 MB. Breite und Höhe misst der Server.',
      notPngLocal: 'Bitte eine PNG-Datei wählen.',
      tooLargeLocal: 'Die Datei ist größer als 5 MB.',
      measured: (w: number, h: number) => `${w} × ${h} px`,
      measuredLocal: (w: number, h: number) => `Gelesen: ${w} × ${h} px (der Server misst noch einmal)`,
      orientation: 'Draufsicht (Oben, Unten): Nase oben. Seitenansicht (Links, Rechts): Nase links. Vorne: Flugzeug von vorn. Innen: Kabine von oben, Nase oben.',
      testPin: 'Der Testpin sitzt in der Bildmitte (0,5 | 0,5).',
      existingPins: (n: number) => `${n} vorhandene ${plural(n, 'Pin', 'Pins')} dieser Ansicht, auf dem neuen Bild`,
      uploadSave: 'Zeichnung speichern',
      uploading: (done: number, total: number) => `Hochladen … Schritt ${done} von ${total}`,
      noTransparency: 'Kein transparenter Hintergrund – auf dunklem Design sieht man einen Kasten.',
      size: 'Größe',
    },
    protocol: {
      entity: 'Entität',
      actor: 'Akteur',
      action: 'Aktion',
      from: 'Von',
      to: 'Bis',
      any: 'Alle',
      system: 'System',
      empty: 'Keine Ereignisse mit diesem Filter.',
      field: 'Feld',
      before: 'Vorher',
      after: 'Nachher',
      raw: 'Rohdaten (JSON)',
      count: (n: number) => `${n} ${plural(n, 'Ereignis', 'Ereignisse')}`,
    },
    trash: {
      empty: 'Der Papierkorb ist leer.',
      deletedBy: (name: string, date: string) => `gelöscht von ${name} · ${date}`,
      kind: { damage: 'Schaden', damage_image: 'Foto', damage_comment: 'Rückfrage/Hinweis', aircraft: 'Flugzeug' } as Record<
        TrashKind,
        string
      >,
    },
    settings: {
      radius: 'Nähe-Radius',
      radiusHint: 'Wie weit ein bestehender Schaden entfernt sein darf, damit er beim Melden angezeigt wird. In Prozent der Zeichnungsbreite.',
      radiusPreview: 'Vorschau auf der DA40 von oben',
      reviewDue: 'Sichtungsfrist',
      reviewDueHint: 'Nach so vielen Tagen erscheint eine Meldung im Eingang als überfällig.',
      days: 'Tage',
    },
  },
  entity: {
    damage: 'Schaden',
    damage_image: 'Foto',
    damage_comment: 'Rückfrage/Hinweis',
    aircraft: 'Flugzeug',
    aircraft_type: 'Muster',
    member: 'Mitglied',
    setting: 'Einstellung',
    tenant: 'Verein',
    aircraft_notice: 'Hinweis der Technik',
  } as Record<EntityType, string>,
  eventAction: {
    created: 'Angelegt',
    updated: 'Geändert',
    status_changed: 'Status geändert',
    image_added: 'Foto hinzugefügt',
    image_removed: 'Foto entfernt',
    merged_report: 'Meldung zusammengeführt',
    deleted: 'Gelöscht',
    restored: 'Wiederhergestellt',
    undo: 'Rückgängig gemacht',
    import: 'Import',
    exported: 'Daten exportiert',
  } as Record<EventAction, string>,
  history: {
    reported: 'Gemeldet',
    created: 'Erfasst',
    edited: 'Bearbeitet',
    moved: 'Position geändert',
    status: (from: string, to: string) => `Status ${from} → ${to}`,
    photosAdded: (n: number) => `${n} ${plural(n, 'Foto', 'Fotos')} hinzugefügt`,
    photoRemoved: 'Foto entfernt',
    logoAdded: 'Vereinslogo hochgeladen',
    logoRemoved: 'Vereinslogo entfernt',
    mergedReport: (n: number) => `Meldung übernommen (${n} ${plural(n, 'Foto', 'Fotos')})`,
    deleted: 'In den Papierkorb gelegt',
    restored: 'Wiederhergestellt',
    undo: (id: number) => `Rückgängig gemacht (Ereignis #${id})`,
    comment: 'Rückfrage/Hinweis geschrieben',
    commentDeleted: 'Rückfrage/Hinweis gelöscht',
    reason: 'Grund',
    exported: 'Vereinsdaten exportiert',
    anonymized: 'Anonymisiert',
    emailChanged: 'E-Mail-Adresse geändert',
    addressAdded: 'Weitere E-Mail-Adresse hinzugefügt',
    addressRemoved: 'Weitere E-Mail-Adresse entfernt',
    noticeResolved: 'Hinweis der Technik aufgehoben',
    drawingUploaded: (view: string) => `Zeichnung ${view} hochgeladen`,
    exportTables: (rows: number, tables: number) =>
      `${rows.toLocaleString('de-DE')} ${plural(rows, 'Zeile', 'Zeilen')} in ${tables} ${plural(tables, 'Tabelle', 'Tabellen')}`,
    nearbySeen: (n: number) => `Beim Melden ${n} ${plural(n, 'Schaden', 'Schäden')} in der Nähe angezeigt`,
    fields: {
      title: 'Titel',
      description: 'Beschreibung',
      category: 'Kategorie',
      categories: 'Kategorien',
      status: 'Status',
      view: 'Ansicht',
      x: 'Position x',
      y: 'Position y',
      reviewNote: 'Notiz',
      mergedInto: 'Zusammengeführt in',
      role: 'Rolle',
      isActive: 'Aktiv',
      displayName: 'Name',
      email: 'E-Mail',
      membersCanReport: 'Mitglieder dürfen melden',
      memberParticipation: 'Mitwirkung der Mitglieder',
      duplicateRadius: 'Nähe-Radius',
      reviewDueDays: 'Sichtungsfrist',
      mediaRequired: 'Foto-Pflicht',
      aircraftGrounding: 'Flugzeuge sperren',
      level: 'Art',
      validUntil: 'Gilt bis',
      registration: 'Kennzeichen',
      isOverview: 'Übersichtsfoto',
      body: 'Text',
      images: 'Fotos',
      count: 'Anzahl Fotos',
      from: 'Aus Meldung',
      note: 'Notiz',
      added: 'Neu',
      updated: 'Geändert',
      deactivated: 'Deaktiviert',
      name: 'Name',
      slug: 'Kurzname',
      code: 'Kürzel',
      notes: 'Notiz',
      clubName: 'Vereinsname',
      street: 'Straße',
      postalCode: 'PLZ',
      city: 'Ort',
      country: 'Land',
      phone: 'Telefon',
      contactEmail: 'E-Mail',
      website: 'Website',
      // Protocol entries from before 06.10.2026 still carry these (ADR 0002).
      vfCid: 'Vereinsflieger-cid',
      vfKeySet: 'Vereinsflieger-Key hinterlegt',
      replaced: 'Ersetzt',
      firstAdmin: 'Erster Administrator',
      typeCode: 'Muster',
      sortOrder: 'Reihenfolge',
      credit: 'Quelle',
      width: 'Breite (px)',
      height: 'Höhe (px)',
      mirroredFrom: 'Gespiegelt aus',
      file: 'Datei',
      size: 'Größe',
      tables: 'Tabellen',
      photos: 'Fotos',
      anonymized: 'Anonymisiert',
    } as Record<string, string>,
    empty: '(leer)',
  },
  time: {
    today: 'heute',
    yesterday: 'gestern',
    daysAgo: (n: number) => `vor ${n} Tagen`,
    minutesAgo: (n: number) => (n <= 1 ? 'gerade eben' : `vor ${n} Minuten`),
    hoursAgo: (n: number) => (n === 1 ? 'vor 1 Stunde' : `vor ${n} Stunden`),
  },
  errors: {
    forbiddenField: 'Diese Änderung ist für deine Rolle nicht erlaubt.',
    notAllowed: 'Für diese Aktion fehlt die Berechtigung.',
    transition: 'Dieser Statuswechsel ist nicht möglich.',
    numbersExhausted: 'Für diesen Verein sind alle Schadensnummern vergeben (bis Z99999).',
    stale: 'Wurde inzwischen geändert – neu laden',
    sessionEnded: 'Die Anmeldung ist abgelaufen. Bitte neu anmelden.',
  },
  demo: {
    band: 'Demo – Daten werden nur in diesem Browser gespeichert.',
    reset: 'Demo zurücksetzen',
    resetConfirm: 'Alle Änderungen an den Demodaten verwerfen und neu beginnen?',
    end: 'Demo beenden',
  },
  export: {
    demoManifestNote: 'Demo-Export: fiktive Daten aus diesem Browser, ohne Fotos.',
    title: 'Vereinsdaten exportieren',
    intro: 'Alle Daten des Vereins als ZIP: jede Tabelle als JSON (mit Änderungsverlauf), dazu die Fotos. Für die Datenübertragbarkeit (DSGVO Art. 20) oder wenn der Verein Außencheck nicht mehr nutzt.',
    platformIntro: 'Export des ganzen Vereins, auch eines deaktivierten (Offboarding). Er steht im Protokoll des Vereins, mit deinem Namen.',
    create: 'Export erstellen',
    creating: 'Export wird erstellt …',
    retention: (days: number) => `Exportdateien werden nach ${days} Tagen gelöscht. Ein Download-Link gilt eine Stunde und wird bei Bedarf neu ausgestellt.`,
    previous: 'Bisherige Exporte',
    none: 'Noch keine Exporte.',
    download: 'Herunterladen',
    renew: 'Link neu anfordern',
    expired: 'Link abgelaufen',
    unavailable: 'Datei nicht mehr verfügbar',
    validUntil: (time: string) => `Link gültig bis ${time}`,
    done: 'Export erstellt.',
    summary: (rows: number, photos: number) => `${rows.toLocaleString('de-DE')} Zeilen, ${photos} ${plural(photos, 'Foto', 'Fotos')}`,
  },
  upload: {
    progress: (done: number, total: number) => `Foto ${Math.min(done + 1, total)} von ${total} wird hochgeladen …`,
    storageRefused: (status: number) => `Der Fotospeicher hat das Foto abgelehnt (${status}).`,
    drawingStorageRefused: (status: number) => `Der Speicher hat die Zeichnung abgelehnt (${status}).`,
    noneUploaded: 'Keines der Fotos konnte hochgeladen werden.',
    photoFailed: (n: number, message: string) => `Foto ${n}: ${message}`,
    someFailed: (failed: number, total: number) =>
      `${failed} von ${total} ${plural(total, 'Foto', 'Fotos')} nicht hochgeladen`,
    retryHint: 'Die markierten Fotos wurden nicht gespeichert. Erneut versuchen oder entfernen.',
  },
  /** The help screens (content: src/help/topics.ts). */
  help: {
    title: 'Hilfe',
    open: 'Hilfe',
    contextLabel: (topic: string) => `Hilfe: ${topic}`,
    intro: 'Alles Wichtige zu Außencheck – auch ohne Verbindung lesbar.',
    search: 'Hilfe durchsuchen',
    searchPlaceholder: 'z. B. Foto, Code, offline …',
    searchEmpty: 'Dazu steht nichts in der Hilfe. Anders formulieren?',
    yourDevice: 'Für dein Gerät',
    yourRole: 'Für deine Rolle',
    otherDevices: 'Andere Geräte',
    groups: {
      start: 'Erste Schritte',
      tasks: 'Aufgaben',
      roles: 'Rollen',
      devices: 'Geräte',
      faq: 'Fragen und Probleme',
    } as Record<string, string>,
    back: 'Alle Themen',
    see: 'Siehe auch',
    notFound: 'Dieses Hilfe-Thema gibt es nicht (mehr).',
    tourStart: 'Kennenlernmodus starten',
    tourHint: 'Die geführte Tour zeigt dir in der Demo Schritt für Schritt, wo was ist.',
    news: 'Neuigkeiten',
    newsIntro: 'Was sich an Außencheck geändert hat, das Neueste zuerst.',
    device: { iphone: 'iPhone', ipad: 'iPad', android: 'Android', web: 'Im Browser' } as Record<string, string>,
    onlyOn: (devices: string) => `Nur: ${devices}`,
    allDevices: 'Hinweise für alle Geräte zeigen',
    thisDevice: 'Nur Hinweise für dieses Gerät',
  },
  /** Coach marks of the guided tour in the demo (steps: src/tour/tours.ts). */
  tour: {
    label: 'Kennenlernmodus',
    position: (index: number, count: number) => `${index} von ${count}`,
    next: 'Weiter',
    back: 'Zurück',
    skip: 'Beenden',
    pause: 'Später weiter',
    done: 'Fertig',
    start: 'Tour starten',
    resume: 'Tour fortsetzen',
    restart: 'Tour neu starten',
    waiting: 'Sobald du das getan hast, geht es weiter.',
    notVisible: 'Dieser Teil ist gerade nicht zu sehen.',
    show: 'Hinführen',
  },
  /** The online sign-up of a club (/verein-anmelden, 0024). Field rules and their messages: @fsv/core signup.ts. */
  signup: {
    kicker: 'Für Vereine',
    title: 'Verein anmelden',
    intro: 'In fünf Schritten zum eigenen Vereinsbereich. Nach der Bestätigung per E-Mail-Code ist der Vertrag geschlossen und euer Bereich sofort eingerichtet.',
    steps: ['Verein', 'Ansprechpartner', 'Flotte & Zahlweise', 'Rechnung', 'Prüfen & abschließen'] as readonly string[],
    stepOf: (n: number, total: number, name: string) => `Schritt ${n} von ${total}: ${name}`,
    next: 'Weiter',
    back: 'Zurück',
    edit: 'Ändern',
    optional: 'optional',
    clubName: 'Name laut Vereinsregister',
    clubNameHint: 'So erscheint er im Vertrag und als Name eures Vereinsbereichs.',
    street: 'Straße und Hausnummer',
    postalCode: 'Postleitzahl',
    city: 'Ort',
    country: 'Land',
    registerCourt: 'Registergericht',
    registerNumber: 'Registernummer',
    registerHint: 'Für eingetragene Vereine, z. B. Amtsgericht Mannheim, VR 1234.',
    representativeName: 'Dein Name',
    representativeRole: 'Deine Funktion im Verein',
    representativeRoleHint: 'Zum Beispiel 1. Vorsitzender, Vorstand, Schatzmeisterin.',
    contactEmail: 'Deine E-Mail-Adresse',
    contactEmailHint: 'Hierhin geht der Bestätigungscode. Mit dieser Adresse meldest du dich danach als Verwaltung eures Vereins an.',
    contactPhone: 'Telefon',
    confirmAuthority: 'Ich bin berechtigt, den Verein zu vertreten und diesen Vertrag für ihn zu schließen.',
    fleetIntro: 'Wie viele Flugzeuge gehören eurem Verein? Private Flugzeuge zählen nicht.',
    fleet: { motor: 'Motorflugzeuge', tmg: 'Motorsegler', glider: 'Segelflugzeuge', ul: 'Ultraleicht (UL)' },
    fleetTmgHint: 'Ein Motorsegler kostet wie ein Motorflugzeug.',
    interval: 'Zahlweise',
    monthly: 'Monatlich',
    yearly: 'Jährlich im Voraus',
    yearlySave: (percent: number) => `${percent} % günstiger`,
    billingNote: 'Abgerechnet werden die vereinseigenen Flugzeuge, die ihr in Außencheck eingetragen habt, jeweils am Ersten eines Monats. Diese Angabe dient nur der ersten Preisangabe.',
    previewTitle: 'Euer Preis',
    previewList: 'Listenpreis',
    previewCap: (cap: string) => `gedeckelt auf ${cap}`,
    previewFounder: (percent: number) => `Pionierverein: −${percent} %`,
    previewFounderHint: 'Solange ein Pionierplatz frei ist, bekommt euer Verein ihn beim Abschluss – dauerhaft, solange der Vertrag ohne Unterbrechung läuft.',
    previewYearly: (percent: number) => `jährlich im Voraus: −${percent} %`,
    perMonth: 'im Monat',
    perYear: 'im Jahr',
    vatIncl: (percent: number) => `inklusive ${percent} % Umsatzsteuer, ohne Einrichtungsgebühr`,
    billingIntro: 'Sollen Rechnungen an eine andere Anschrift oder E-Mail-Adresse gehen? Sonst gehen sie an den Verein und an dich.',
    billingDifferent: 'Andere Rechnungsanschrift',
    billingName: 'Name / Empfänger',
    billingEmail: 'E-Mail für Rechnungen',
    vatId: 'USt-IdNr.',
    summaryClub: 'Verein',
    summaryContact: 'Ansprechpartner',
    summaryFleet: 'Flotte & Zahlweise',
    summaryBilling: 'Rechnung',
    summaryBillingDefault: 'An den Verein und an deine E-Mail-Adresse',
    acceptAgbPre: 'Ich stimme den ',
    acceptAgbLink: 'AGB für Vereine',
    acceptAgbPost: (v: string) => ` (Fassung ${v}) zu.`,
    acceptAvvPre: 'Ich schließe für den Verein den ',
    acceptAvvLink: 'Vertrag zur Auftragsverarbeitung',
    acceptAvvPost: (v: string) => ` (Fassung ${v}) nach Art. 28 DSGVO.`,
    termsNote: 'Für eure Mitglieder gelten die Nutzungsbedingungen; ihr informiert sie darüber.',
    submit: 'Kostenpflichtig abschließen',
    submitHint: 'Danach schicken wir dir einen Code per E-Mail. Erst mit dem Code ist der Vertrag geschlossen.',
    sending: 'Wird gesendet …',
    codeTitle: 'Code eingeben',
    codeSentTo: (email: string) => `Wir haben einen Bestätigungscode an ${email} geschickt.`,
    codeNotDelivered: 'Die E-Mail konnte gerade nicht verschickt werden. Fordere gleich einen neuen Code an.',
    codeValid: (minutes: number) => `Der Code ist ${minutes} Minuten gültig. Schau auch im Spam-Ordner nach.`,
    code: 'Bestätigungscode',
    confirm: 'Vertrag bestätigen',
    resend: 'Neuen Code senden',
    resent: 'Ein neuer Code ist unterwegs.',
    wrongEmail: 'Falsche Adresse? Zurück zum Formular',
    doneTitle: 'Willkommen bei Außencheck!',
    doneBody: (club: string, number: string) => `Der Vertrag für ${club} ist geschlossen (Vertragsnummer ${number}). Euer Vereinsbereich ist eingerichtet.`,
    doneFounder: (rank: number, total: number) => `Ihr habt Pionierplatz ${rank} von ${total}.`,
    doneMail: 'Die Vertragsbestätigung mit AGB und AVV ist per E-Mail unterwegs.',
    doneMailFailed: 'Die Vertragsbestätigung konnte gerade nicht verschickt werden; wir schicken sie dir nach.',
    nextTitle: 'So geht es weiter',
    nextSteps: [
      'Melde dich mit deiner E-Mail-Adresse an – du bekommst einen Anmeldecode, ein Passwort gibt es nicht.',
      'Trage unter Verwaltung → Flotte eure Flugzeuge ein; bei einem eigenen Muster gibst du dort seine Klasse an.',
      'Lege unter Verwaltung → Mitglieder eure Mitglieder an oder importiere die Liste aus Vereinsflieger.',
    ] as readonly string[],
    signIn: 'Jetzt anmelden',
    alreadyMember: 'Schon dabei?',
    signInLink: 'Anmelden',
    demoLink: 'Erst die Demo ansehen',
    contactLink: 'Fragen? Schreib uns',
    loadFailed: 'Die Anmeldung ist gerade nicht erreichbar. Bitte versuch es später noch einmal.',
    stepErrors: 'Bitte prüfe die markierten Angaben.',
    restart: 'Neu anmelden',
  },
  /** Contracts in the platform area (0024). */
  contracts: {
    nav: 'Verträge',
    tenantsNav: 'Vereine',
    title: 'Verträge',
    subtitle: 'Online geschlossene und von Hand erfasste Verträge. Die Pionierplätze vergibt das System beim Abschluss.',
    create: 'Vertrag erfassen',
    createTitle: 'Vertrag für bestehenden Verein erfassen',
    createIntro: 'Für Verträge, die außerhalb des Formulars geschlossen wurden – etwa der FSV 1910 als Pionierverein 1. Der Verein muss schon angelegt sein.',
    tenant: 'Verein',
    tenantChoose: 'Verein wählen',
    empty: 'Noch keine Verträge.',
    count: (n: number) => `${n} ${plural(n, 'Vertrag', 'Verträge')}`,
    founderPlaces: (left: number, total: number) => `Pionierplätze frei: ${left} von ${total}`,
    status: { pending: 'Wartet auf Code', active: 'Aktiv', terminated: 'Gekündigt', expired: 'Abgelaufen' } as const,
    source: { online: 'Online', manual: 'Von Hand' } as const,
    founder: 'Pionierverein',
    founderShort: 'Platz',
    operatorTitle: 'Vertragsangaben',
    founderRank: (rank: number) => `Pionierplatz ${rank}`,
    founderNone: 'Kein Pionierplatz',
    founderAuto: 'Nächster freier Platz',
    founderExact: 'Platz',
    interval: { monthly: 'monatlich', yearly: 'jährlich im Voraus' } as const,
    number: 'Vertragsnummer',
    concludedAt: 'Geschlossen',
    startsOn: 'Beginn',
    concludedOn: 'Geschlossen am',
    endsOn: 'Ende',
    terminatedAt: 'Gekündigt am',
    fee: 'Gebühr laut Anmeldung',
    versions: 'Fassungen',
    notes: 'Notizen (intern)',
    save: 'Speichern',
    saved: 'Vertrag gespeichert.',
    created: 'Vertrag erfasst.',
    back: 'Alle Verträge',
    terminate: 'Kündigen',
    terminateTitle: (club: string) => `Vertrag mit ${club} kündigen?`,
    terminateBody: 'Der Vertrag endet zum angegebenen Datum. Der Vereinsbereich bleibt offen, bis du ihn sperrst; nach dem AVV werden die Daten 90 Tage nach Vertragsende gelöscht (Runbook).',
    terminated: 'Vertrag gekündigt.',
    block: 'Verein sperren',
    unblock: 'Sperre aufheben',
    blocked: 'Gesperrt',
    blockHint: 'Gesperrt kann niemand den Vereinsbereich öffnen – bei mehreren Bereichen alle; die Daten bleiben erhalten.',
    departments: 'Bereiche des Vereins',
    departmentsHint:
      'Ein Verein in Abteilungen bekommt je Abteilung einen eigenen Bereich mit eigener Verwaltung, eigenen Mitgliedern und eigener Flotte. Alle Bereiche gehören zu diesem Vertrag: Die Abrechnung zählt ihre Flugzeuge gemeinsam, jedes Kennzeichen einmal, mit einem Deckel und einem Pionierrabatt.',
    mainArea: 'Erster Bereich',
    addDepartment: 'Bereich hinzufügen',
    departmentChoose: 'Bereich wählen',
    departmentNone: 'Kein freier Bereich – erst unter „Vereine“ anlegen',
    newArea: 'Neuen Bereich anlegen',
    departmentAdded: 'Bereich hinzugefügt.',
    removeDepartment: 'Entfernen',
    removeDepartmentTitle: (name: string) => `„${name}“ aus dem Vertrag nehmen?`,
    removeDepartmentBody:
      'Der Bereich bleibt mit allen Daten bestehen, wird aber ab dem nächsten 1. nicht mehr mit diesem Vertrag abgerechnet. Ohne eigenen Vertrag erscheint er in der Abrechnung als „kein Vertrag“.',
    departmentRemoved: 'Bereich entfernt.',
    openClub: 'Verein in der Plattform',
    contact: 'Ansprechpartner',
    billing: 'Rechnung',
    declared: 'Angegebene Flotte',
  },
  /** The "Neuigkeiten" cards after signing in (entries: @fsv/core ANNOUNCEMENTS, words: src/news/texts.ts). */
  news: {
    title: 'Neu in Außencheck',
    titleVersion: (version: string) => `Neu in Version ${version}`,
    position: (index: number, count: number) => `${index} von ${count}`,
    next: 'Weiter',
    back: 'Zurück',
    dismiss: 'Verstanden',
    laterHint: 'Alle Neuigkeiten stehen auch in der Hilfe.',
  },
} as const;
